import { useState } from 'react';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubscribeStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d6cr8uaohb161tfd86s0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubscribeStatus('success');
        setEmail('');
      } else {
        setSubscribeStatus('error');
      }
    } catch {
      setSubscribeStatus('error');
    }
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const navbarHeight = 120;
      const elementPosition = element.getBoundingClientRect().top + window.scrollY;
      const targetY = elementPosition - navbarHeight;

      const flipFn = (window as any).__bbFlipScroll;
      if (typeof flipFn === 'function') {
        flipFn(targetY);
      } else {
        window.scrollTo({ top: targetY, behavior: 'smooth' });
      }
    }
  };

  return (
    <footer className="relative overflow-hidden bg-ocean-mid text-white">
      {/* Background decorative elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-1/4 w-80 h-80 bg-sunset-coral rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-sunset-peach rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-5 gap-12 mb-12">
          <div>
            <div className="flex items-center space-x-3 mb-4">
              <img
                src="https://storage.readdy-site.link/project_files/11bf54dc-cd8b-412f-9383-b1020052ccc0/e790e585-9341-4eda-8466-7e9d89aa3fd5_ChatGPT-Image-Mar-2-2026-12_02_26-PM-ek.png?v=e86025dbcd928cb45795c7555e605914"
                alt="BB Beyhan Balance"
                className="h-14 w-auto object-contain brightness-110"
              />
            </div>
            <p className="text-sm text-white/80 italic mb-6">Always by your side, ready to support, guide, and ease the load</p>

            <form id="newsletter-form" data-readdy-form onSubmit={handleSubscribe} className="mb-6">
              <div className="flex">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email"
                  required
                  className="flex-1 px-4 py-2.5 rounded-l-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-sunset-coral text-sm"
                />
                <button
                  type="submit"
                  className="bg-gradient-to-r from-sunset-coral to-sunset-orange text-white px-4 py-2.5 rounded-r-lg hover:shadow-lg hover:shadow-sunset-coral/30 transition-all font-semibold text-sm cursor-pointer whitespace-nowrap"
                >
                  Subscribe
                </button>
              </div>
              {subscribeStatus === 'success' && (
                <p className="text-xs text-sunset-peach mt-2">Subscribed successfully!</p>
              )}
              {subscribeStatus === 'error' && (
                <p className="text-xs text-sunset-peach mt-2">Something went wrong.</p>
              )}
            </form>

            <div className="flex items-center space-x-3">
              <a
                href="https://www.linkedin.com/notifications/?filter=all"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="w-10 h-10 border border-white/30 rounded-full flex items-center justify-center hover:bg-sunset-coral/20 hover:border-sunset-coral transition-all cursor-pointer"
              >
                <i className="ri-linkedin-line"></i>
              </a>
              <a
                href="mailto:info@beyhanbalance.com"
                className="w-10 h-10 border border-white/30 rounded-full flex items-center justify-center hover:bg-sunset-coral/20 hover:border-sunset-coral transition-all cursor-pointer"
              >
                <i className="ri-mail-line"></i>
              </a>
              <a
                href="https://wa.me/31685297511"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="w-10 h-10 border border-white/30 rounded-full flex items-center justify-center hover:bg-sunset-coral/20 hover:border-sunset-coral transition-all cursor-pointer"
              >
                <i className="ri-whatsapp-line"></i>
              </a>
            </div>
          </div>

          <div>
            <h4 className="font-bold text-white mb-4 text-sm">
              <a href="#services" onClick={(e) => { e.preventDefault(); scrollToSection('services'); }} className="cursor-pointer">Services</a>
            </h4>
            <ul className="space-y-2.5 text-sm">
              {[
                'Bookkeeping',
                'VAT Filing & Review',
                'Financial Control',
                'Financial Accounting Support',
                'Accounts Payable & Receivable',
                'Bank Monitoring & Payment Processing',
              ].map((item) => (
                <li key={item}>
                  <a
                    href="#services"
                    onClick={(e) => { e.preventDefault(); scrollToSection('services'); }}
                    className="text-white/70 hover:text-sunset-coral transition-colors cursor-pointer"
                  >
                    {item}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-bold text-white mb-4 text-sm">
              <a href="#about" onClick={(e) => { e.preventDefault(); scrollToSection('about'); }} className="cursor-pointer">Company</a>
            </h4>
            <ul className="space-y-2.5 text-sm">
              {[
                { label: 'About BB Global Administration', id: 'about' },
                { label: 'How We Support', id: 'services' },
                { label: 'Where We Are', id: 'global' },
                { label: 'References & Feedback', id: 'references-testimonials' },
                { label: 'Contact', id: 'contact' },
              ].map((item) => (
                <li key={item.id}>
                  <a
                    href={`#${item.id}`}
                    onClick={(e) => { e.preventDefault(); scrollToSection(item.id); }}
                    className="text-white/70 hover:text-sunset-coral transition-colors cursor-pointer"
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-bold text-white mb-4 text-sm">Software Tools</h4>
            <ul className="space-y-2.5 text-sm">
              <li className="text-white/70">Exact Online</li>
              <li className="text-white/70">Microsoft Dynamics 365</li>
              <li className="text-white/70">SAP Ariba</li>
            </ul>
          </div>

          <div>
            <h4 className="font-bold text-white mb-4 text-sm">
              <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }} className="cursor-pointer">Get in Touch</a>
            </h4>
            <ul className="space-y-3 text-sm">
              <li>
                <a href="mailto:info@beyhanbalance.com" className="text-white hover:text-sunset-coral transition-colors cursor-pointer">
                  info@beyhanbalance.com
                </a>
              </li>
              <li>
                <a href="tel:+31685297511" className="text-white/80 hover:text-sunset-coral transition-colors cursor-pointer">
                  +31 6 85297511
                </a>
              </li>
              <li className="text-white/70">Amsterdam 1065 LH</li>
              <li className="flex items-center space-x-2">
                <span className="text-lg">🇳🇱</span>
                <span className="text-lg">🇹🇷</span>
                <span className="text-lg">🌍</span>
                <span className="text-white/70">Worldwide</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/20 pt-8">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <div className="text-sm text-white/70">
              &copy; {new Date().getFullYear()} BB Beyhan Balance. All rights reserved.
            </div>

            <div className="flex items-center space-x-4 text-sm text-white/70">
              <span className="text-xl">🇳🇱</span>
              <span className="text-xl">🇹🇷</span>
              <span className="text-xl">🌍</span>
              <span className="text-white/50">Worldwide</span>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer nofollow" className="hover:text-sunset-coral transition-colors cursor-pointer">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
